import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ToggleSwitch from './ToggleSwitch';
import { X } from 'lucide-react';

const SettingsPanel = ({
  isOpen,
  onClose,
  theme,
  onThemeChange,
  fontSize,
  onFontSizeChange,
  density,
  onDensityChange,
  isHighlightingEnabled,
  onToggleHighlighting,
  isSampleHighlightingEnabled,
  onToggleSampleHighlighting,
  onLogout,
  user,
}) => {
  const panelRef = useRef(null);

  // Close on click outside the panel (but no blur overlay)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Small delay so the opening click doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <div 
      ref={panelRef}
      className={`fixed top-0 right-0 h-full w-72 z-50 flex flex-col shadow-2xl shadow-black/40 transition-transform duration-250 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      style={{ 
        backgroundColor: 'var(--bg-surface)', 
        borderLeft: '1px solid var(--border-primary)',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
      }}
    >
      <div className="flex items-center justify-between px-4 py-3.5" style={{ borderBottom: '1px solid var(--border-primary)' }}>
        <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Settings</h2>
        <button 
          onClick={onClose} 
          className="p-1.5 rounded-lg transition-all duration-150"
          style={{ color: 'var(--text-faint)' }}
          title="Close Settings"
        >
          <X size={16} />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4 space-y-5">
        {/* Font Size */}
        <div className="space-y-2">
          <label className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Font Size</label>
          <div className="flex items-center gap-1">
            {['14', '16', '18'].map((size) => (
              <button
                key={size}
                onClick={() => onFontSizeChange(parseInt(size, 10))}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  fontSize === parseInt(size, 10) 
                    ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' 
                    : ''
                }`}
                style={fontSize !== parseInt(size, 10) ? { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-secondary)' } : {}}
              >
                {size === '14' ? 'S' : size === '16' ? 'M' : 'L'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-0.5">
            <input
              type="range"
              min="12"
              max="20"
              step="1"
              value={fontSize}
              onChange={(e) => onFontSizeChange(parseInt(e.target.value, 10))}
              className="flex-1 h-1 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              style={{ backgroundColor: 'var(--border-primary)' }}
            />
            <span className="text-[10px] min-w-[30px] tabular-nums" style={{ color: 'var(--text-faint)' }}>{fontSize}px</span>
          </div>
        </div>

        <div className="h-px" style={{ backgroundColor: 'var(--border-primary)' }} />

        {/* Density */}
        <div className="space-y-2">
          <label className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Density</label>
          <div className="flex gap-1">
            {['normal', 'compact', 'super-compact'].map((d) => (
              <button
                key={d}
                onClick={() => onDensityChange(d)}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  density === d 
                    ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' 
                    : ''
                }`}
                style={density !== d ? { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-secondary)' } : {}}
              >
                {d === 'super-compact' ? 'Dense' : d === 'normal' ? 'Normal' : 'Compact'}
              </button>
            ))}
          </div>
        </div>
        
        <div className="h-px" style={{ backgroundColor: 'var(--border-primary)' }} />

        {/* Highlighting */}
        <div className="space-y-1">
          <label className="text-[11px] font-medium uppercase tracking-wider block mb-2" style={{ color: 'var(--text-faint)' }}>Highlighting</label>
          <ToggleSwitch label="Highlight Term" isChecked={isHighlightingEnabled} onToggle={onToggleHighlighting} />
          <ToggleSwitch label="Highlight Samples" isChecked={isSampleHighlightingEnabled} onToggle={onToggleSampleHighlighting} />
        </div>

        <div className="h-px" style={{ backgroundColor: 'var(--border-primary)' }} />

        {/* Actions */}
        <div className="space-y-1">
          <label className="text-[11px] font-medium uppercase tracking-wider block mb-2" style={{ color: 'var(--text-faint)' }}>Actions</label>
          {user?.role === 'admin' && (
            <Link 
              to="/admin" 
              className="block w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-150"
              style={{ color: 'var(--text-secondary)' }}
              onClick={onClose}
            >
              Admin Panel
            </Link>
          )}
          <button 
            onClick={onLogout} 
            className="w-full text-left px-3 py-2 text-sm text-rose-400/80 hover:bg-rose-500/10 hover:text-rose-300 rounded-lg transition-all duration-150"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;